/**
 * Skeletal pose maths for the entity models.
 *
 * Pure: no Babylon imports, no state, no clock. This is where every animation
 * decision lives so it can be unit tested without a WebGL context — the same
 * split as `game/lerp.ts`. The rig in `rig.ts` does nothing but write these
 * numbers onto transform nodes.
 *
 * Angles are radians. Positive pitch swings a limb *forward* (toward +Z in the
 * model's local space, which is the direction the entity faces).
 */

/**
 * Every joint the models expose. A single flat struct rather than a tree,
 * because it makes blending two poses a fixed list of lerps with no allocation
 * beyond the one result object.
 */
export interface Pose {
  /** Vertical bob of the whole body, in world units. */
  torsoY: number;
  /** Lean forward (positive) or back (negative). */
  torsoPitch: number;
  /** Twist of the torso around the vertical axis. */
  torsoYaw: number;
  headPitch: number;
  headYaw: number;
  /** Weapon arm. */
  armRPitch: number;
  /** Off arm — shield for the crusader. */
  armLPitch: number;
  armRYaw: number;
  /**
   * Yaw of the off arm, the mirror of `armRYaw`. Added with the elbows (Phase 41): a shield
   * arm that could only swing forward and back could never open a shout or reach across a
   * two-handed haft.
   */
  armLYaw: number;
  legRPitch: number;
  legLPitch: number;
  /**
   * Forearm pitch relative to the upper arm — the elbow. Positive bends the forearm
   * *forward*, which is the only way an elbow bends, so these live in [0, ~2.2].
   *
   * **Elbows and knees are what made the box rig read as a mannequin.** A single straight
   * segment from shoulder to hand cannot cock a blow, draw a bow, hold a spell in front of
   * the chest or tuck into a roll; it can only pivot, and every animation looked like a
   * doll's arm swinging on a pin. Phase 41 split each limb in two and gave the pose a
   * channel for the joint between them.
   */
  elbowR: number;
  elbowL: number;
  /**
   * Shin pitch relative to the thigh — the knee. A knee bends *backward*, so a bent knee is
   * **negative** here: the same "positive is forward" convention every other pitch follows,
   * rather than a second sign rule for one joint.
   */
  kneeR: number;
  kneeL: number;
  /** Extra rotation of the weapon within the hand, for the swing follow-through. */
  weaponPitch: number;
  /** Roll of the whole model — only death uses it, to topple sideways. */
  rootRoll: number;
  /** How far the whole model has sunk, in world units. Death only. */
  rootSink: number;
  /**
   * How far the **off hand has let go** of a two-hander's grip, 0 → 1. A spell cast with the free
   * hand (`attribute`, `invoke`, `utility`) takes it off the haft to throw, flare or clench; at 0
   * `applyPose` keeps it solved onto the grip (`reachOffHand`). Every other pose leaves it 0.
   */
  offHandFree: number;
}

/** A pose with every joint at rest. Never mutate the export — copy it. */
export const REST_POSE: Readonly<Pose> = Object.freeze({
  torsoY: 0,
  torsoPitch: 0,
  torsoYaw: 0,
  headPitch: 0,
  headYaw: 0,
  armRPitch: 0,
  armLPitch: 0,
  armRYaw: 0,
  armLYaw: 0,
  legRPitch: 0,
  legLPitch: 0,
  elbowR: 0,
  elbowL: 0,
  kneeR: 0,
  kneeL: 0,
  weaponPitch: 0,
  rootRoll: 0,
  rootSink: 0,
  offHandFree: 0,
});

/** Allocates a fresh mutable pose at rest. Used once per entity, not per frame. */
export function restPose(): Pose {
  return { ...REST_POSE };
}

// ── Tunables ────────────────────────────────────────────────────────────────

/** How far the legs swing at full stride, radians. */
const STRIDE = 0.62;

/** How far the arms counter-swing while walking. */
const ARM_SWING = 0.42;

/** Vertical bob at the top of each step, world units. */
const WALK_BOB = 0.055;

/** Breathing depth while idle. */
const IDLE_BOB = 0.022;

/** Seconds for one full idle breath. */
const IDLE_PERIOD = 3.4;

/** The resting bend in an elbow, radians. Straight arms are what a mannequin has. */
const IDLE_ELBOW = 0.18;

/** How far a knee bends as the leg swings through under the body while walking. */
const WALK_KNEE = 0.7;

const TAU = Math.PI * 2;

// ── Poses ───────────────────────────────────────────────────────────────────

/**
 * Standing still: a slow breath and a barely-there sway, so an idle entity
 * still reads as alive rather than as a statue.
 *
 * `t` is seconds; the result is periodic with IDLE_PERIOD.
 */
export function idlePose(t: number, out: Pose = restPose()): Pose {
  const breath = Math.sin((t / IDLE_PERIOD) * TAU);
  const sway = Math.sin((t / (IDLE_PERIOD * 1.7)) * TAU);

  out.torsoY = breath * IDLE_BOB;
  out.torsoPitch = breath * 0.02;
  out.torsoYaw = sway * 0.03;
  out.headPitch = breath * -0.03;
  out.headYaw = sway * 0.08;
  out.armRPitch = breath * 0.04;
  out.armLPitch = breath * -0.04;
  out.armRYaw = 0;
  out.armLYaw = 0;
  out.legRPitch = 0;
  out.legLPitch = 0;
  // A resting arm is never dead straight — a slight, breathing bend is most of what tells a
  // figure from a mannequin when nothing else is moving.
  out.elbowR = IDLE_ELBOW + breath * 0.03;
  out.elbowL = IDLE_ELBOW - breath * 0.03;
  out.kneeR = 0;
  out.kneeL = 0;
  out.weaponPitch = 0;
  out.rootRoll = 0;
  out.rootSink = 0;
  return out;
}

/**
 * Walk cycle. `phase` is the position in the stride, wrapped to [0, 1).
 *
 * The legs are exactly half a cycle apart and the arms counter-swing against
 * them, which is what makes a walk read as a walk. The body bobs twice per
 * cycle — once per footfall.
 */
export function walkPose(phase: number, out: Pose = restPose()): Pose {
  const p = wrap01(phase);
  const swing = Math.sin(p * TAU);

  out.legRPitch = swing * STRIDE;
  out.legLPitch = -swing * STRIDE;
  // Arms oppose the legs on the same side.
  out.armRPitch = -swing * ARM_SWING;
  out.armLPitch = swing * ARM_SWING;
  out.armRYaw = 0;
  out.armLYaw = 0;
  // Elbows carry a walking bend and cock a little further as the arm comes forward.
  out.elbowR = IDLE_ELBOW + Math.max(0, -swing) * 0.35;
  out.elbowL = IDLE_ELBOW + Math.max(0, swing) * 0.35;
  // **The knee bends on the forward swing only.** A leg swinging through under the body
  // lifts the foot by folding the knee, then lands straight — so the bend follows the
  // *velocity* of the stride (cos), not its position (sin), and each knee is bent for
  // exactly the half-cycle its leg is moving forward. A knee that bent on both halves
  // would read as pedalling.
  const lift = Math.cos(p * TAU);
  out.kneeR = -WALK_KNEE * Math.max(0, lift);
  out.kneeL = -WALK_KNEE * Math.max(0, -lift);

  // Two footfalls per cycle, so the bob is at double frequency and always
  // downward from the standing height.
  out.torsoY = -Math.abs(Math.sin(p * TAU)) * WALK_BOB;
  out.torsoPitch = 0.1;
  out.torsoYaw = swing * 0.06;
  out.headPitch = -0.04;
  out.headYaw = swing * -0.05;
  out.weaponPitch = 0;
  out.rootRoll = 0;
  out.rootSink = 0;
  return out;
}

/**
 * Attack swing, driven by the server's cooldown so the blade is where the
 * damage is.
 *
 * `swing` is the raw cooldown: **1 at the instant the blow lands**, counting
 * down to 0 when the next one is ready. So the cycle runs backwards from
 * contact:
 *
 * ```
 *   swing 1.0  contact — arm extended forward and down
 *         0.55 recovered to guard
 *         0.0  wound up, blade raised, ready
 *         ↓
 *        (server applies damage, cooldown resets to 1)
 * ```
 *
 * The strike itself — raised to extended — happens in the single tick the
 * cooldown resets. That is not a cheat: a sword blow *is* the fast part, and
 * placing it on the damage tick is what makes the hit read as connected.
 */
export function attackPose(swing: number, out: Pose = restPose()): Pose {
  const s = clamp01(swing);

  // Time since contact: 0 at the blow, 1 when ready to strike again.
  const t = 1 - s;

  /** Fraction of the cooldown spent recovering before the next windup starts. */
  const FOLLOW_THROUGH = 0.45;

  const CONTACT = 0.85;
  const GUARD = -0.35;
  const WINDUP = -2.1;

  let armPitch: number;
  let weapon: number;
  let lean: number;
  let elbow: number;

  if (t < FOLLOW_THROUGH) {
    // Following through from the blow back to a guard.
    const k = easeOutCubic(t / FOLLOW_THROUGH);
    armPitch = lerpN(CONTACT, GUARD, k);
    weapon = lerpN(0.5, 0, k);
    lean = lerpN(0.22, 0, k);
    elbow = lerpN(0.15, 0.6, k);
  } else {
    // Raising the weapon for the next blow.
    const k = easeInOutSine((t - FOLLOW_THROUGH) / (1 - FOLLOW_THROUGH));
    armPitch = lerpN(GUARD, WINDUP, k);
    weapon = lerpN(0, -0.9, k);
    lean = lerpN(0, -0.12, k);
    // **The elbow is what makes it a blow.** Cocked to 1.3 rad behind the shoulder at the
    // wind-up, driven to nearly straight at contact: a blade delivered by a straight arm
    // swinging on a pin is a pendulum, and that is exactly what the box rig had.
    elbow = lerpN(0.6, 1.3, k);
  }

  out.armRPitch = armPitch;
  out.armRYaw = -0.35;
  out.armLPitch = 0.25;
  // The off arm is up and bent — a shield held across the body, or a free hand guarding.
  out.armLYaw = 0.3;
  out.elbowR = elbow;
  out.elbowL = 0.9;
  out.weaponPitch = weapon;
  out.torsoPitch = lean;
  out.torsoYaw = -lean * 0.9;
  out.torsoY = 0;
  out.headPitch = lean * 0.4;
  out.headYaw = 0;
  out.legRPitch = -0.18;
  out.legLPitch = 0.22;
  // A fighting stance has soft knees; a locked one is a statue about to fall over.
  out.kneeR = -0.2;
  out.kneeL = -0.25;
  out.rootRoll = 0;
  out.rootSink = 0;
  return out;
}

/**
 * A bow's basic attack: drawn and loosed, on the same clock as `attackPose`.
 *
 * Phase 41. The generic swing raised the weapon arm behind the head and brought it down,
 * which swings a bow like a club — reported as the bow *"not feeling real"* in the hand,
 * and no grip angle could fix a bow being swung. So a rig whose weapon is held `bow`
 * style (`Rig.hold`) plays this instead, chosen in `ProceduralModel.poseFor`; the pose
 * maths still knows nothing about weapons, it just has one more shape.
 *
 * Same convention as the swing: `swing` is the cooldown, **1 at the instant the arrow
 * leaves**, so the cycle runs backwards from the loose —
 *
 * ```
 *   swing 1.0  loose — bow arm level, draw hand snapped forward along it
 *         0.65 lowered a little, both arms easing
 *         0.0  fully drawn: bow arm level, draw hand at the cheek, body side-on
 * ```
 *
 * The draw hand's travel is almost all **elbow**, which is why this pose could not exist
 * on the seven-joint rig: an arm that only pivots at the shoulder cannot reach a cheek
 * with the shoulder still square to the target.
 */
export function drawPose(swing: number, out: Pose = restPose()): Pose {
  const s = clamp01(swing);
  const t = 1 - s;

  /** Fraction of the cooldown spent easing off the loose before the next draw begins. */
  const SETTLE = 0.35;

  /** The bow arm: level and forward, the whole cycle. */
  const AIM = -1.5;

  let bowArm: number;
  let drawArm: number;
  let drawElbow: number;
  let side: number;

  if (t < SETTLE) {
    // Just loosed: the draw hand is forward along the bow arm, then both relax a little.
    const k = easeOutCubic(t / SETTLE);
    bowArm = lerpN(AIM, AIM + 0.35, k);
    drawArm = lerpN(-1.3, -0.9, k);
    drawElbow = lerpN(0.3, 0.6, k);
    side = lerpN(0.55, 0.35, k);
  } else {
    // Drawing: the bow arm comes back to level and the draw hand folds to the cheek.
    const k = easeInOutSine((t - SETTLE) / (1 - SETTLE));
    bowArm = lerpN(AIM + 0.35, AIM, k);
    drawArm = lerpN(-0.9, -1.4, k);
    drawElbow = lerpN(0.6, 2.0, k);
    side = lerpN(0.35, 0.55, k);
  }

  out.armRPitch = bowArm;
  out.armRYaw = -0.25;
  out.elbowR = 0.08;
  out.armLPitch = drawArm;
  out.armLYaw = -0.3;
  out.elbowL = drawElbow;
  // The bow stays upright in the hand: the *arm* aims, the wrist does not flick.
  out.weaponPitch = 0;
  out.torsoPitch = -0.05;
  out.torsoYaw = side;
  out.torsoY = 0;
  out.headPitch = -0.1;
  out.headYaw = -side * 0.7;
  // Side-on stance, feet apart, knees soft.
  out.legRPitch = -0.28;
  out.legLPitch = 0.28;
  out.kneeR = -0.18;
  out.kneeL = -0.22;
  out.rootRoll = 0;
  out.rootSink = 0;
  return out;
}

/**
 * Death. `progress` runs 0 → 1 once and then holds — the caller must clamp and
 * never restart it, or the corpse twitches.
 *
 * The body folds, topples sideways and sinks slightly into the ground.
 */
export function deathPose(progress: number, out: Pose = restPose()): Pose {
  const p = clamp01(progress);
  const fall = easeOutBounceless(p);

  out.rootRoll = fall * (Math.PI / 2);
  out.rootSink = fall * 0.34;
  out.torsoY = -fall * 0.22;
  out.torsoPitch = fall * 0.5;
  out.torsoYaw = fall * 0.3;
  out.headPitch = fall * 0.7;
  out.headYaw = fall * 0.25;
  // Limbs go slack rather than staying posed.
  out.armRPitch = fall * 1.1;
  out.armLPitch = fall * 0.8;
  out.armRYaw = fall * 0.4;
  out.armLYaw = -fall * 0.3;
  out.legRPitch = fall * 0.55;
  out.legLPitch = fall * 0.3;
  out.elbowR = fall * 0.5;
  out.elbowL = fall * 0.7;
  out.kneeR = -fall * 0.45;
  out.kneeL = -fall * 0.25;
  out.weaponPitch = fall * 1.2;
  return out;
}

/**
 * How a skill looks when it goes off.
 *
 * **Eight movements for a hundred skills, and that ratio is the design.** The first
 * four were chosen by `scaling_type` alone — the same field that decides a skill's
 * damage — which meant adding a skill needed no animation work but also that a
 * hundred skills shared four movements. The catalogue names 88 distinct
 * `animation_clip` ids, and the tempting reading of "one animation per skill" is 88
 * clips; that would be the wrong thing to build. At the gameplay camera a character
 * is roughly 30 × 45 device pixels, where posture and mass read and detail does not,
 * so 88 near-identical wind-ups would be strictly more work for a player who could
 * not tell any two of them apart.
 *
 * So the clip ids map onto a small set of genuinely distinct silhouettes, and the
 * set grew from four to eight rather than to eighty-eight. Every pair is measured
 * apart in `poses.test.ts` — the same discipline `bolts.test.ts` applies to
 * projectile shapes, and for the same reason: two things a player cannot tell apart
 * are one thing.
 *
 * The four new ones fill gaps the scaling type could not express:
 *
 *  - `slam` — driven straight down. The only cast that *drops* the body.
 *  - `shout` — chest open, head thrown back. The only one that opens backwards.
 *  - `sweep` — a horizontal whip. The only one whose torso yaw dominates, and the
 *    only one where it changes sign through the cast.
 *  - `aim` — drawn and released. The only one with the two arms on opposite sides
 *    of rest.
 */
export type CastStyle =
  | 'weapon'
  | 'attribute'
  | 'hybrid'
  | 'utility'
  | 'slam'
  | 'shout'
  | 'sweep'
  | 'aim'
  | 'invoke';

/** Every cast style, so a test can walk the set rather than restate it. */
export const CAST_STYLES: readonly CastStyle[] = [
  'weapon',
  'attribute',
  'hybrid',
  'utility',
  'slam',
  'shout',
  'sweep',
  'aim',
  'invoke',
];

/**
 * Which movement a catalogue `animation_clip` id resolves to.
 *
 * **An exceptions table, deliberately.** A clip whose scaling-type default already
 * reads correctly is absent from it: a fireball is a channelled `attribute` cast
 * whether or not `anim_cast_fireball` is listed here. What the table holds is every
 * clip the scaling type would get *wrong* — a ground slam that would otherwise be an
 * overhead swing, a warcry that would otherwise be a channel, a bow shot that would
 * otherwise be a thrust.
 *
 * An id that is not here falls back to `castStyleFor(scalingType)`, exactly as an
 * absent id does. That is what keeps the original property intact: **a new skill
 * still needs no animation work**, and authoring one that wants a particular movement
 * is one line here rather than a new clip.
 *
 * The server never reads any of it. `animation_clip` is passed through untouched,
 * like `particle_emitter` — and unlike `projectile_shape` it is not a closed
 * vocabulary, because closing it would mean a Go edit for every new skill.
 */
export const CAST_ANIMATIONS: Readonly<Record<string, CastStyle>> = Object.freeze({
  // Driven down into the ground: slams, stomps, planted shields, laid traps.
  anim_ground_slam: 'slam',
  anim_stomp: 'slam',
  anim_fissure_slam: 'slam',
  anim_leap_slam: 'slam',
  anim_slam_2h_heavy: 'slam',
  anim_hammer_slam_fire: 'slam',
  anim_earth_shock_slam: 'slam',
  anim_thunder_clap: 'slam',
  anim_bastion_plant: 'slam',
  anim_lay_trap: 'slam',
  anim_life_break_leap: 'slam',
  anim_drop_bomb: 'slam',

  // Opened backwards: shouts, roars, taunts, and the two transformations, which are
  // the same gesture at a larger scale.
  anim_shout: 'shout',
  anim_bellow_shout: 'shout',
  anim_primal_roar: 'shout',
  anim_taunt_gesture: 'shout',
  anim_bloodlust_yell: 'shout',
  anim_second_wind: 'shout',
  anim_stamina_surge: 'shout',
  anim_frenzy_buff: 'shout',
  anim_cleanse_burst: 'shout',
  anim_colossus_transform: 'shout',
  anim_avatar_grow: 'shout',

  // Whipped horizontally: cleaves, spins, flurries.
  anim_cleave_arc: 'sweep',
  anim_whirlwind_loop: 'sweep',
  anim_gods_rebuke_sweep: 'sweep',
  anim_blade_fury_spin: 'sweep',
  anim_whirl_daggers: 'sweep',
  anim_fan_knives_burst: 'sweep',
  anim_omnislash_multi: 'sweep',
  anim_spectral_slash: 'sweep',
  anim_5hit_combo: 'sweep',

  // Drawn and released: bows, crossbows, slings, and anything thrown overarm.
  anim_bow_shoot: 'aim',
  anim_bow_trickshot: 'aim',
  anim_sling_shoot: 'aim',
  anim_crossbow_channel: 'aim',
  anim_sniper_aim_shoot: 'aim',
  anim_magic_arrow_shot: 'aim',
  anim_throw_chain: 'aim',
  anim_throw_orb: 'aim',

  // Invoked over the ground ahead (2026-09-28): the arms flare up in a V, then snap down toward it.
  // Novas, storms, rifts, meteors, walls and consecrated ground — a spell that lands *somewhere*
  // rather than flying at someone.
  anim_nova_cast: 'invoke',
  anim_blizzard_channel: 'invoke',
  anim_sanctify_ground: 'invoke',
  anim_void_rift: 'invoke',
  anim_summons_meteor: 'invoke',
  anim_singularity_channel: 'invoke',
  anim_wall_raise: 'invoke',
  anim_gravity_pull: 'invoke',
  anim_sanctuary_circle: 'invoke',
  anim_call_light: 'invoke',
  anim_sunray_channel: 'invoke',
  anim_beam_sky: 'invoke',

  // Warded: attribute-scaled skills that are buffs on the caster, not bolts thrown at anyone.
  anim_buff_self: 'utility',
  anim_shield_reflect: 'utility',
  anim_buff_shield: 'utility',
  anim_blood_to_mana: 'utility',
});

/**
 * Maps a catalogue `scaling_type` to a cast animation.
 *
 * The fallback half of the registry, and still the whole answer for most of the
 * catalogue. `PASSIVE` never reaches here — a passive cannot be cast — but it maps to
 * the utility flourish rather than throwing, because an unknown value arriving from a
 * future catalogue should look slightly wrong, not crash the render loop.
 */
export function castStyleFor(scalingType: string): CastStyle {
  switch (scalingType) {
    case 'WEAPON':
      return 'weapon';
    case 'ATTRIBUTE':
      return 'attribute';
    case 'HYBRID':
      return 'hybrid';
    default:
      return 'utility';
  }
}

/**
 * Which movement a skill uses: its clip's, or its scaling type's.
 *
 * The registry first and the scaling type second, in that order, because the point of
 * the clip id is to override a default that reads wrongly. An id nobody has mapped
 * behaves exactly as it did before the registry existed.
 */
export function castStyleForClip(clip: string | undefined, scalingType: string): CastStyle {
  if (clip !== undefined) {
    const mapped = CAST_ANIMATIONS[clip];
    if (mapped !== undefined) return mapped;
  }
  return castStyleFor(scalingType);
}

/**
 * Where in the animation the commit lands: the frame the blow connects.
 *
 * Slightly before halfway, because a wind-up shorter than the recovery reads as
 * decisive.
 */
export const CAST_COMMIT = 0.4;

/** How much of a spell's wind-up is the coil; the rest is the snap out to full extension. */
export const SPELL_COIL = 0.7;
/** How much of a spell's recovery holds the extension before settling. */
export const SPELL_HOLD = 0.15;

/**
 * The shortest wind-up a cast animation gets, in seconds.
 *
 * An instant skill has no wind-up in the *simulation* — the damage lands in the tick
 * the key went down — but a strike with no wind-up on screen reads as a twitch rather
 * than as a blow, so the animation keeps one and it is retroactive licence. This is
 * `CAST_SECONDS × CAST_COMMIT`, which is to say exactly the wind-up every cast had
 * before cast times existed.
 */
export const CAST_WINDUP_MIN = 0.22;

/** How long the body takes to settle after the commit, in seconds. */
export const CAST_RECOVER = 0.33;

/**
 * How long a cast animation plays, for a skill with no cast time.
 *
 * Derived rather than declared, so the instant case is the `castTimeSec = 0` end of
 * one formula rather than a second rule. 0.55 s — the fixed length every cast
 * animation had before there was anything to match it to.
 */
export const CAST_SECONDS = CAST_WINDUP_MIN + CAST_RECOVER;

/**
 * How long a cast animation plays, given the skill's cast time.
 *
 * **This is the explicit relationship between the two**, and it needed to be explicit:
 * once a cast takes 0.85 s server-side, an animation that still took 0.55 s would
 * finish while the character was still held, leaving them standing in idle for the
 * back third of their own ultimate.
 *
 * The wind-up *is* the cast — stretched to fill it, floored at `CAST_WINDUP_MIN` so an
 * instant skill keeps the movement it always had — and the recovery is a fixed
 * follow-through on the end. So the commit frame lands on the tick the server resolves,
 * which is the same rule `attackPose` follows by putting contact on the damage tick.
 */
export function castAnimSeconds(castTimeSec = 0): number {
  return castWindup(castTimeSec) + CAST_RECOVER;
}

/**
 * Turns elapsed animation time into the 0 → 1 progress `castPose` wants.
 *
 * Piecewise, because the two halves stretch differently: the wind-up occupies
 * `[0, CAST_COMMIT]` of the pose however long it really lasts, and the recovery
 * occupies the rest. Without the remap a long cast would spend 72% of its animation on
 * the wind-up as a *fraction* and the pose would commit far too early — the strike
 * would land a third of a second before the damage.
 */
export function castProgressFor(elapsedSeconds: number, castTimeSec = 0): number {
  if (!Number.isFinite(elapsedSeconds) || elapsedSeconds <= 0) return 0;

  const windup = castWindup(castTimeSec);
  if (elapsedSeconds < windup) return CAST_COMMIT * (elapsedSeconds / windup);

  const settled = (elapsedSeconds - windup) / CAST_RECOVER;
  return clamp01(CAST_COMMIT + (1 - CAST_COMMIT) * settled);
}

/** The wind-up's real length: the cast itself, never shorter than the floor. */
function castWindup(castTimeSec: number): number {
  if (!Number.isFinite(castTimeSec) || castTimeSec < CAST_WINDUP_MIN) return CAST_WINDUP_MIN;
  return castTimeSec;
}

/**
 * A skill going off. `progress` runs 0 → 1, with the commit at `CAST_COMMIT`.
 *
 * Every style shares one shape — wind up, commit, recover — because that is what
 * makes a cast readable at a glance from an isometric camera. What differs is
 * *which* limbs do it and how far, so the eight are distinguishable at the
 * distance the game is actually played at rather than only in close-up.
 *
 * Progress is normalised rather than a time, which is what lets one pose serve a
 * 0.85 s ultimate and an instant strike: `castProgressFor` owns the mapping from real
 * seconds onto this parameter, and the commit is always at 40% of it wherever that
 * falls in wall-clock time.
 */
export function castPose(style: CastStyle, progress: number, out: Pose = restPose()): Pose {
  const p = clamp01(progress);

  // The commit lands at 40%, not halfway: a wind-up slightly shorter than the
  // recovery reads as decisive. For a windowed cast it is also the tick the server
  // resolved on — see `castProgressFor`.
  const COMMIT = CAST_COMMIT;
  const release = p < COMMIT ? 0 : easeOutCubic((p - COMMIT) / (1 - COMMIT));

  /**
   * Two envelopes, and **both start and end at zero**.
   *
   * That is the whole discipline here: a cast is an overlay on whatever the body
   * was already doing, so anything left non-zero at either end is a snap back to
   * idle that the player sees. `peak` is the wind-up, full at the commit; `strike`
   * is a bump through the recovery, for the styles that throw something.
   */
  const peak = p < COMMIT ? easeInOutSine(p / COMMIT) : 1 - release;
  const strike = 4 * release * (1 - release);

  /**
   * **The spell styles' two envelopes** (2026-09-28): `coil` and `snap`, and full extension lands
   * *on* the commit — the tick the spell goes off — not in the middle of the recovery, where
   * `strike` peaks. The first 70 % of the wind-up coils (the telegraph), the last 30 % snaps out
   * of it, the extension holds briefly, and the recovery settles. Both start and end at zero.
   * Reported: *the casting animation looks like someone shrugging* — the channel it replaced rose
   * into a chest-high hold and sank back, with no release anywhere in it.
   */
  const coilEnd = COMMIT * SPELL_COIL;
  const out1 = (p - coilEnd) / (COMMIT - coilEnd);
  const after = (p - COMMIT) / (1 - COMMIT);
  const coil = p < coilEnd ? easeInOutSine(p / coilEnd) : p < COMMIT ? 1 - easeOutCubic(out1) : 0;
  const snap =
    p < coilEnd ? 0 : p < COMMIT ? easeOutCubic(out1) : after < SPELL_HOLD ? 1 : 1 - easeInOutSine((after - SPELL_HOLD) / (1 - SPELL_HOLD));
  const c = coil;
  const t = snap;
  out.offHandFree = 0;

  switch (style) {
    case 'weapon':
      // A two-handed overhead: wound up over the head, then driven down. Bigger
      // than the basic swing in every axis, which is the reason to spend stamina.
      out.armRPitch = -2.4 * peak + 1.6 * strike;
      out.armLPitch = -2.0 * peak + 1.2 * strike;
      out.armRYaw = -0.15 * peak;
      out.weaponPitch = -1.1 * peak + 0.9 * strike;
      out.torsoPitch = -0.25 * peak + 0.5 * strike;
      out.torsoYaw = 0;
      out.torsoY = 0.06 * peak;
      out.headPitch = -0.1 * peak + 0.25 * strike;
      out.headYaw = 0;
      out.legRPitch = -0.3 * peak;
      out.legLPitch = 0.34 * peak;
      out.armLYaw = 0.15 * peak;
      out.elbowR = 0.9 * peak;
      out.elbowL = 0.7 * peak;
      out.kneeR = -0.25 * peak;
      out.kneeL = -0.25 * peak;
      break;

    case 'attribute':
      // **Thrown** — a bolt, a beam, anything flown at a target. The free hand draws back behind
      // the shoulder with the spine twisted away and the weight on the back foot, then snaps
      // straight out at the target as the body lunges after it; the weapon arm braces. The free
      // hand, because it is the aiming hand and the one the gathered light sits in.
      out.armLPitch = -1.3 * c + 1.6 * t;
      out.armLYaw = 0.35 * c - 0.3 * t;
      out.elbowL = 1.5 * c + 0.1 * t;
      out.armRPitch = 0.35 * c + 0.25 * t;
      out.armRYaw = 0;
      out.elbowR = 0.7 * c + 0.6 * t;
      out.weaponPitch = 0;
      // A positive torso yaw carries the left shoulder back: the coil, then through past square.
      out.torsoYaw = 0.55 * c - 0.45 * t;
      out.torsoPitch = -0.15 * c + 0.35 * t;
      out.torsoY = 0.02 * c - 0.03 * t;
      // The head stays on the target while the shoulders turn under it.
      out.headYaw = -0.35 * c + 0.3 * t;
      out.headPitch = -0.05 * c + 0.15 * t;
      out.legRPitch = 0.25 * (c + t);
      out.legLPitch = -0.2 * (c + t);
      out.kneeR = -0.15 * (c + t);
      out.kneeL = -0.25 * c;
      out.offHandFree = Math.max(c, t);
      break;

    case 'invoke':
      // **Invoked** over the ground ahead: both arms flare up and out in a V with the chest
      // arched and the body lifting, then everything snaps down and forward toward where the
      // spell lands, the knees dipping under it. A staff goes up with the arm and comes down
      // like a rod.
      out.armRPitch = 2.6 * c + 1.3 * t;
      out.armLPitch = 2.6 * c + 1.3 * t;
      out.armRYaw = -0.7 * c + 0.15 * t;
      out.armLYaw = 0.7 * c - 0.15 * t;
      out.elbowR = 0.25 * c + 0.1 * t;
      out.elbowL = 0.25 * c + 0.1 * t;
      out.weaponPitch = 0.3 * t;
      out.torsoPitch = -0.35 * c + 0.35 * t;
      out.torsoYaw = 0;
      out.torsoY = 0.07 * c - 0.12 * t;
      out.headPitch = -0.5 * c + 0.35 * t;
      out.headYaw = 0;
      out.legRPitch = 0.15 * c + 0.3 * t;
      out.legLPitch = -0.1 * t;
      out.kneeR = -0.1 * c - 0.3 * t;
      out.kneeL = -0.35 * t;
      out.offHandFree = Math.max(c, t);
      break;

    case 'hybrid':
      // A stepping thrust: the weapon arm coils across the body, then drives
      // forward as the shoulders come round.
      out.armRPitch = -1.3 * peak + 1.0 * strike;
      out.armLPitch = 0.6 * peak;
      out.armRYaw = -0.7 * peak + 0.6 * strike;
      out.weaponPitch = -0.45 * peak + 0.4 * strike;
      out.torsoPitch = -0.15 * peak + 0.45 * strike;
      out.torsoYaw = 0.4 * peak - 0.5 * strike;
      out.torsoY = -0.05 * peak;
      out.headPitch = 0.2 * strike;
      out.headYaw = -0.2 * peak + 0.25 * strike;
      out.legRPitch = -0.15 * peak - 0.5 * strike;
      out.legLPitch = 0.2 * peak + 0.4 * strike;
      out.armLYaw = 0.2 * peak;
      // Coiled at the elbow, then driven straight through the thrust — never past straight.
      out.elbowR = Math.max(0, 1.0 * peak - 0.9 * strike);
      out.elbowL = 0.5 * peak;
      out.kneeR = -0.2 * peak - 0.2 * strike;
      out.kneeL = -0.2 * peak;
      break;

    case 'slam':
      // Both arms straight overhead and the weight rocked back, then everything
      // driven down at once. **The only cast that drops the body**, which is what
      // separates it from the overhead `weapon` swing at a glance: that one leads
      // with a weapon arm and stays upright, this one is symmetrical and folds.
      //
      // Not to be confused with a roll: the legs bend under the smash but one foot
      // plants forward, where a tuck brings both knees up together. `tucked()` in
      // poses.test.ts is the predicate that holds the two apart.
      out.armRPitch = -2.9 * peak + 3.3 * strike;
      out.armLPitch = -2.9 * peak + 3.3 * strike;
      out.armRYaw = 0;
      out.weaponPitch = -0.8 * peak + 1.6 * strike;
      out.torsoPitch = -0.7 * peak + 1.0 * strike;
      out.torsoYaw = 0;
      out.torsoY = 0.18 * peak - 0.42 * strike;
      out.headPitch = -0.5 * peak + 0.9 * strike;
      out.headYaw = 0;
      out.legRPitch = 0.25 * peak + 0.55 * strike;
      out.legLPitch = 0.25 * peak - 0.3 * strike;
      out.armLYaw = 0;
      out.elbowR = 0.6 * peak;
      out.elbowL = 0.6 * peak;
      out.kneeR = -0.35 * peak - 0.5 * strike;
      out.kneeL = -0.3 * peak;
      break;

    case 'shout':
      // Chest thrown open, head right back, arms flung wide and behind. **The only
      // animation the body has that opens backwards**, which is what makes a roar
      // read as a roar from above rather than as another raised-arm cast. It reaches
      // for nothing, so like the channelled spell it has no strike — only a small
      // lift through the recovery, as the shout carries.
      out.armRPitch = -0.8 * peak;
      out.armLPitch = -0.8 * peak;
      out.armRYaw = 1.1 * peak;
      out.weaponPitch = 0.35 * peak;
      out.torsoPitch = -0.55 * peak;
      out.torsoYaw = 0;
      out.torsoY = 0.22 * peak + 0.06 * strike;
      out.headPitch = -0.95 * peak;
      out.headYaw = 0;
      out.legRPitch = -0.12 * peak;
      out.legLPitch = 0.12 * peak;
      // Flung wide on both sides: the off arm mirrors the weapon arm's yaw.
      out.armLYaw = -1.1 * peak;
      out.elbowR = 0.45 * peak;
      out.elbowL = 0.45 * peak;
      out.kneeR = -0.1 * peak;
      out.kneeL = -0.1 * peak;
      break;

    case 'sweep':
      // Coiled one way and whipped the other. **Torso yaw dominates and changes
      // sign**, which nothing else here does — a cleave and a spin are the same
      // gesture at the size this renders at, and both are unmistakable against a
      // vertical swing.
      out.armRPitch = -0.6 * peak + 0.55 * strike;
      out.armLPitch = 0.35 * peak - 0.3 * strike;
      out.armRYaw = -1.0 * peak + 1.5 * strike;
      out.weaponPitch = -0.6 * peak + 1.0 * strike;
      out.torsoPitch = 0.12 * peak + 0.2 * strike;
      out.torsoYaw = -0.85 * peak + 1.6 * strike;
      out.torsoY = -0.04 * peak;
      out.headPitch = 0.1 * strike;
      out.headYaw = -0.5 * peak + 0.7 * strike;
      out.legRPitch = 0.25 * peak - 0.35 * strike;
      out.legLPitch = -0.25 * peak + 0.35 * strike;
      out.armLYaw = 0.6 * peak - 0.8 * strike;
      out.elbowR = 0.45 * peak;
      out.elbowL = 0.6 * peak;
      out.kneeR = -0.25 * peak;
      out.kneeL = -0.25 * peak;
      break;

    case 'aim':
      // Bow arm out level, draw arm back past the shoulder, body turned side-on.
      // **The only style that pushes the weapon arm forward to wind up** — every other
      // one pulls it back — which is the whole read for a shot, and a shape no swing or
      // channel can produce. (Not merely "the arms are opposed": `hybrid` and `sweep`
      // are opposed too, in the other order. The direction is what is unique.)
      out.armRPitch = 0.75 * peak + 0.45 * strike;
      out.armLPitch = -1.55 * peak + 0.5 * strike;
      out.armRYaw = -0.9 * peak;
      out.weaponPitch = 0.25 * peak - 0.55 * strike;
      out.torsoPitch = -0.05 * peak;
      out.torsoYaw = 0.55 * peak - 0.25 * strike;
      out.torsoY = 0.03 * peak;
      out.headPitch = -0.12 * peak;
      out.headYaw = 0.35 * peak;
      out.legRPitch = -0.3 * peak;
      out.legLPitch = 0.3 * peak;
      // The bow arm is straight; the draw hand is folded right back to the cheek and snaps
      // straight on the release. Never past straight.
      out.armLYaw = -0.35 * peak;
      out.elbowR = 0.1 * peak;
      out.elbowL = Math.max(0, 1.9 * peak - 1.4 * strike);
      out.kneeR = -0.2 * peak;
      out.kneeL = -0.2 * peak;
      break;

    default:
      // **Warded** — a buff on the caster. Upright and inward, and small: the free hand clenches
      // to the chest while the weapon holds its guard, then the chest opens in a short pulse.
      // Nothing below the hips, so a ward cast on the run keeps running (`walkUnder`).
      out.armLPitch = 0.9 * c + 0.55 * t;
      out.armLYaw = -0.95 * c + 0.55 * t;
      out.elbowL = 1.9 * c + 0.35 * t;
      out.armRPitch = 0.55 * c + 0.45 * t;
      out.armRYaw = 0.2 * c - 0.45 * t;
      out.elbowR = 1.1 * c + 0.4 * t;
      out.weaponPitch = 0;
      out.torsoPitch = 0.22 * c - 0.15 * t;
      out.torsoYaw = 0;
      out.torsoY = -0.05 * c + 0.05 * t;
      out.headPitch = 0.25 * c - 0.2 * t;
      out.headYaw = 0;
      out.legRPitch = 0;
      out.legLPitch = 0;
      out.kneeR = -0.15 * c;
      out.kneeL = -0.15 * c;
      out.offHandFree = Math.max(c, t);
      break;
  }

  out.rootRoll = 0;
  out.rootSink = 0;
  return out;
}

/** How long the dodge roll takes to play out, from launch back to standing. */
export const DODGE_SECONDS = 0.3;

/**
 * The dodge roll. `progress` runs 0 → 1 across `DODGE_SECONDS`.
 *
 * A tuck and a recover: the body drops and pitches hard forward, both legs come
 * up together, the arms wrap in, and then it all unwinds into a landing. Three
 * tenths of a second is barely nine frames of animation at the tick rate, so the
 * shape has to be one unmistakable silhouette rather than a sequence — a
 * character roughly 30 × 45 device pixels on screen shows posture and mass and
 * nothing finer.
 *
 * **Symmetry is the whole read.** Every other animation the body has is
 * one-sided — a swing, a thrust, a raised casting arm — so legs tucking *together*
 * is the one thing that cannot be mistaken for any of them at that size.
 *
 * It deliberately leaves `rootRoll` and `rootSink` alone, despite a roll being
 * the obvious use for the first. `rootRoll` topples the model sideways, which is
 * a cartwheel rather than a forward roll, and those two channels are the ones the
 * corpse uses: a dodge that shared them would read as the beginning of a death,
 * which is precisely the frame a player must never misread mid-fight. Sinking the
 * root would also push the body through the floor it is rolling along.
 */
export function dodgePose(progress: number, out: Pose = restPose()): Pose {
  const p = clamp01(progress);

  /** Where the body is fully compressed. Early — the launch is the explosive part. */
  const APEX = 0.35;

  /**
   * Two envelopes, and — as with `castPose` — **both start and end at zero**,
   * because a dodge is an overlay on whatever the body was doing and anything left
   * behind is a snap back to idle the player sees.
   *
   * `tuck` is the compression, full at the apex: in fast, out slower, which is what
   * makes the roll read as launched rather than as a squat. `plant` is a bump
   * through the recovery for the legs splitting to catch the landing — the only
   * asymmetry in the pose, and it happens after the silhouette has done its work.
   */
  const recover = p < APEX ? 0 : (p - APEX) / (1 - APEX);
  const tuck = p < APEX ? easeOutCubic(p / APEX) : 1 - easeInOutSine(recover);
  const plant = 4 * recover * (1 - recover);

  // Deeper in every axis than any swing or cast: the largest torso pitch, and the
  // only animation that drops the body this far without dying.
  out.torsoPitch = 1.15 * tuck;
  out.torsoY = -0.3 * tuck - 0.05 * plant;
  out.torsoYaw = 0;
  out.headPitch = 0.85 * tuck;
  out.headYaw = 0;

  // Arms forward and in around the tuck, not raised.
  //
  // This *was* "opposite in sign to every cast", and it stopped being true when the
  // cast set grew: `aim` draws one arm back past the shoulder and `slam` drives both
  // arms down through its recovery, so an arm-sign rule no longer separates a roll
  // from a cast. What still does is the whole silhouette — knees up **together** with
  // the body dropped further than any living animation drops it — which is what
  // `tucked()` in poses.test.ts measures against all eight styles at every frame.
  out.armRPitch = 1.0 * tuck;
  out.armLPitch = 1.0 * tuck;
  out.armRYaw = 0;
  out.armLYaw = 0;
  out.elbowR = 1.2 * tuck;
  out.elbowL = 1.2 * tuck;
  out.weaponPitch = 0.6 * tuck;

  // Knees to chest, together, then split to land.
  out.legRPitch = 1.5 * tuck - 0.5 * plant;
  out.legLPitch = 1.5 * tuck + 0.35 * plant;
  // Folded hard — a tuck is knees *and* shins — and the planted leg lands bent.
  out.kneeR = -1.3 * tuck - 0.3 * plant;
  out.kneeL = -1.3 * tuck;

  out.rootRoll = 0;
  out.rootSink = 0;
  return out;
}

/**
 * Blends two poses, writing into `out`. Used to cross-fade on a state change so
 * a mob does not snap from chase to attack.
 */
export function blendPose(a: Pose, b: Pose, t: number, out: Pose = restPose()): Pose {
  const k = clamp01(t);
  out.torsoY = lerpN(a.torsoY, b.torsoY, k);
  out.torsoPitch = lerpN(a.torsoPitch, b.torsoPitch, k);
  out.torsoYaw = lerpN(a.torsoYaw, b.torsoYaw, k);
  out.headPitch = lerpN(a.headPitch, b.headPitch, k);
  out.headYaw = lerpN(a.headYaw, b.headYaw, k);
  out.armRPitch = lerpN(a.armRPitch, b.armRPitch, k);
  out.armLPitch = lerpN(a.armLPitch, b.armLPitch, k);
  out.armRYaw = lerpN(a.armRYaw, b.armRYaw, k);
  out.armLYaw = lerpN(a.armLYaw, b.armLYaw, k);
  out.legRPitch = lerpN(a.legRPitch, b.legRPitch, k);
  out.legLPitch = lerpN(a.legLPitch, b.legLPitch, k);
  out.elbowR = lerpN(a.elbowR, b.elbowR, k);
  out.elbowL = lerpN(a.elbowL, b.elbowL, k);
  out.kneeR = lerpN(a.kneeR, b.kneeR, k);
  out.kneeL = lerpN(a.kneeL, b.kneeL, k);
  out.weaponPitch = lerpN(a.weaponPitch, b.weaponPitch, k);
  out.rootRoll = lerpN(a.rootRoll, b.rootRoll, k);
  out.rootSink = lerpN(a.rootSink, b.rootSink, k);
  out.offHandFree = lerpN(a.offHandFree, b.offHandFree, k);
  return out;
}

/**
 * How far the stride advances for a given distance travelled.
 *
 * Driving the walk cycle off distance rather than time is what stops the feet
 * skating: at half speed the legs swing at half rate, automatically.
 */
export function advanceStride(phase: number, distance: number, strideLength = 1.55): number {
  return wrap01(phase + distance / strideLength);
}

// ── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Linear interpolation that returns its endpoints *exactly*.
 *
 * `a + (b - a) * 1` is not bit-identical to `b` in floating point, and
 * `blendPose` at t = 1 must land precisely on the target pose or a finished
 * cross-fade leaves a permanent sub-degree offset on every joint. Same contract
 * as `lerp()` in game/lerp.ts.
 */
function lerpN(a: number, b: number, t: number): number {
  if (t <= 0) return a;
  if (t >= 1) return b;
  return a + (b - a) * t;
}

function clamp01(v: number): number {
  if (!Number.isFinite(v)) return 0;
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/** Wraps into [0, 1), correctly for negative input. */
function wrap01(v: number): number {
  if (!Number.isFinite(v)) return 0;
  return ((v % 1) + 1) % 1;
}

function easeOutCubic(t: number): number {
  const k = 1 - t;
  return 1 - k * k * k;
}

function easeInOutSine(t: number): number {
  return 0.5 - Math.cos(Math.PI * t) / 2;
}

/**
 * A fall that decelerates into the ground without bouncing. A bounce would read
 * as comedy, which is not the register this game is in.
 */
function easeOutBounceless(t: number): number {
  return 1 - (1 - t) * (1 - t) * (1 - t);
}

/**
 * Whirlwind: arms and weapon flung out level, the body leaning into the turn.
 *
 * Phase 30.4. **The rotation is not here.** A pose describes the body's parts relative to its own
 * root, and the spin is the *root* turning — `EntityLayer` owns `root.rotation.y` because that is
 * also where facing lives, and two writers for one angle is how a body ends up pointing at the
 * average of two answers. So this is the shape the spin happens to, not the spin.
 *
 * `phase` is the spin's own angle in radians, and it is used for the parts that should *not* turn
 * with the body: a small counter-bob so the model reads as spinning rather than as a rigid object
 * on a turntable, which at the gameplay camera is most of the difference.
 *
 * The arms are level rather than raised. A whirlwind's weapon traces a horizontal circle at hip
 * height — raised arms read as a cast, which is exactly the confusion this pose exists to end.
 */
export function spinPose(phase: number, out: Pose = restPose()): Pose {
  // Two bobs per revolution: the body rises and dips as its weight shifts across the turn.
  const bob = Math.sin(phase * 2);

  out.torsoY = bob * 0.035;
  // Leaning *out* of the turn, the way anything spinning fast does. Constant rather than
  // oscillating: it is centrifugal, and it does not care where in the revolution the body is.
  out.torsoPitch = -0.12;
  out.torsoYaw = 0.35;
  out.headPitch = 0.05;
  // The head trails the shoulders slightly — it is the one part that is not committed.
  out.headYaw = -0.25;

  // Both arms out level. `armRYaw` swings the weapon arm wide of the body so the blade traces a
  // circle outside the silhouette rather than through it.
  out.armRPitch = -Math.PI / 2;
  out.armRYaw = -1.15;
  out.armLPitch = -Math.PI / 2;
  out.armLYaw = 1.15;
  out.elbowR = 0.25;
  out.elbowL = 0.25;
  // Level within the hand, so the blade lies along the circle it is cutting.
  out.weaponPitch = -0.35;

  // A wide, braced stance, with a little scissoring so the legs are not frozen.
  out.legRPitch = 0.16 + bob * 0.06;
  out.legLPitch = -0.16 - bob * 0.06;
  out.kneeR = -0.2;
  out.kneeL = -0.2;
  out.rootRoll = 0;
  out.rootSink = 0;
  return out;
}
